import { BaseCharacterState, CreatureType, PlayerState } from "mmo-common";
import { CharacterGameObject } from "./character.gameObject";
import { Quat, Vector3 } from "realms-engine";
import { mapCharacterStateToAnimation } from "./mapCharacterStateToAnimation";
import { PlayerFloatingWeapon } from "../floating-weapon/playerFloatingWeapon.gameObject";
import { MMONetworkSlave } from "@utils/types";
import { MMOCreature } from "@game/models/mmoCreature";

export interface OtherPlayerGameObjectEventsMap {
  inCombatChange: { inCombat: boolean };
}

/*
 * Manages the players other than yourself
 */
export class OtherPlayerGameObject
  extends CharacterGameObject<OtherPlayerGameObjectEventsMap>
  implements MMONetworkSlave<CreatureType.Character>, MMOCreature
{
  clientId: string;
  characterId: number;
  inRangeInteractableIds: number[];

  private floatingWeapon: PlayerFloatingWeapon;

  private masterPlayerState: PlayerState;

  constructor(state: PlayerState, options: { shouldHideUI?: boolean } = {}) {
    super(state, options);
    this.clientId = state.clientId;
    this.characterId = state.characterId;
    this.inRangeInteractableIds = state.inRangeInteractableIds;

    this.masterPlayerState = state;

    this.floatingWeapon = this.addChild(
      new PlayerFloatingWeapon({ playerState: state })
    );
    this.floatingWeapon.addListener("onShowChange", (show) => {
      this.characterGameObject.setWeaponVisibility(!show);
      this.playCharacterStateAnimation(this.getMasterPlayerState());
    });

    this.characterGameObject.setWeaponVisibility(
      !this.floatingWeapon.isShowing()
    );
  }

  override mapStateToAnimation(state: BaseCharacterState) {
    return mapCharacterStateToAnimation(state, {
      showingFloatingWeapon: Boolean(this.floatingWeapon.isShowing()),
    });
  }

  getCharacterId() {
    return this.characterId;
  }

  /**
   * Whether the player is in combat
   */
  isInCombat() {
    return this.getMasterPlayerState().inCombat;
  }

  /**
   * Gets the position of the player
   */
  getPosition(): Vector3 {
    return this.getMasterPlayerState().creature.position;
  }

  /**
   * Gets the quaternion of the player
   */
  getQuaternion(): Quat {
    return this.getMasterPlayerState().creature.quaternion;
  }

  /**
   * Get the master player state
   */
  getMasterPlayerState() {
    return this.masterPlayerState;
  }

  override onSlaveChanges(
    changes: Partial<PlayerState>,
    newState: PlayerState,
    oldState: PlayerState
  ): void {
    this.masterPlayerState = newState;

    if (changes.clientId !== undefined) {
      this.clientId = newState.clientId;
    }

    if (changes.inCombat !== undefined) {
      this.dispatch("inCombatChange", {
        inCombat: changes.inCombat,
      });
    }

    if (changes.characterId !== undefined) {
      this.characterId = newState.characterId;
    }

    if (changes.inRangeInteractableIds !== undefined) {
      this.inRangeInteractableIds = changes.inRangeInteractableIds;
    }

    if (changes.character?.weapon !== undefined) {
      this.floatingWeapon.setWeapon(newState.character.weapon);
    }

    if (
      changes.creature?.position !== undefined ||
      changes.creature?.quaternion !== undefined
    ) {
      this.floatingWeapon.updateFloatingWeaponPosition({
        quaternion: newState.creature.quaternion,
        position: newState.creature.position,
      });
    }

    super.onSlaveChanges(changes, newState, oldState);
  }

  override playCharacterStateAnimation(state: PlayerState): void {
    this.floatingWeapon.updateFloatingWeaponState(state);
    super.playCharacterStateAnimation(state);
  }
}
